import { FeatureFlag, FeatureFlagKey, UpdateFeatureFlagPayload } from "../../models/featureFlag";
import { API_ENDPOINTS } from "../../utils/api-service";
import { FEATURE_FLAG_ORDER } from "../../utils/constants/feature-flag-constants";
import { api } from "./api";

const featureFlagTag = (tenantId: string, flagKey: string) => ({
  type: "featureFlags" as const,
  id: `${tenantId}:${flagKey}`,
});

const featureFlagListTag = (tenantId: string) => ({
  type: "featureFlags" as const,
  id: `LIST:${tenantId}`,
});

const unwrapApiData = (response: unknown): unknown => {
  if (typeof response !== "object" || response === null) {
    return response;
  }

  const record = response as Record<string, unknown>;
  if (record.success === true && record.data !== undefined) {
    return record.data;
  }

  return response;
};

const toInt = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
};

const toBoolean = (value: unknown): boolean => {
  if (value === false || value === 0 || value === "0" || value === "false") {
    return false;
  }
  return value === true || value === 1 || value === "1" || value === "true";
};

const mapFeatureFlag = (raw: unknown): FeatureFlag => {
  const row = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;

  return {
    id: toInt(row.id),
    tenantId: String(row.tenantId ?? row.tenant_id ?? ""),
    flagKey: String(row.flagKey ?? row.flag_key ?? "") as FeatureFlagKey,
    enabled: toBoolean(row.enabled),
    rolloutPct: toInt(row.rolloutPct ?? row.rollout_pct, 100),
    updatedAt: String(row.updatedAt ?? row.updated_at ?? ""),
    updatedBy:
      row.updatedBy == null && row.updated_by == null
        ? null
        : toInt(row.updatedBy ?? row.updated_by),
  };
};

const normalizeListResponse = (response: unknown): FeatureFlag[] => {
  const unwrapped = unwrapApiData(response);
  if (!Array.isArray(unwrapped)) {
    return [];
  }
  return unwrapped.map(mapFeatureFlag).filter((flag) => FEATURE_FLAG_ORDER.includes(flag.flagKey));
};

export const featureFlagsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getFeatureFlagsByTenant: build.query<FeatureFlag[], string>({
      query: (tenantId) => ({
        url: API_ENDPOINTS.FEATURE_FLAGS.BY_TENANT(tenantId),
      }),
      transformResponse: (response: unknown) => normalizeListResponse(response),
      providesTags: (result, _error, tenantId) =>
        result
          ? [
              ...result.map((flag) => featureFlagTag(tenantId, flag.flagKey)),
              featureFlagListTag(tenantId),
            ]
          : [featureFlagListTag(tenantId)],
    }),

    updateFeatureFlag: build.mutation<FeatureFlag, UpdateFeatureFlagPayload>({
      query: ({ tenantId, flagKey, enabled, rolloutPct }) => ({
        url: API_ENDPOINTS.FEATURE_FLAGS.UPDATE(tenantId, flagKey),
        method: "PUT",
        body: {
          enabled,
          rollout_pct: rolloutPct,
        },
      }),
      transformResponse: (response: unknown) => mapFeatureFlag(unwrapApiData(response)),
      invalidatesTags: (_result, _error, { tenantId, flagKey }) => [
        featureFlagTag(tenantId, flagKey),
        featureFlagListTag(tenantId),
      ],
    }),
  }),
});

export const {
  useGetFeatureFlagsByTenantQuery,
  useUpdateFeatureFlagMutation,
} = featureFlagsApi;
